import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";

import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationsRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../../services/notificationsApi";

// ─── State ────────────────────────────────────────────────────────────────────



/**
 * Minimal shape this slice needs from the root state. Declared locally to avoid a
 * circular `store/index` <-> slice type import while keeping selectors typed.
 */


const initialState = {
  items: [],
  status: "idle",
  error: null,
  lastFetchedAt: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

/**
 * Loads the full notification list. Used on mount and by the polling loop.
 * `silent` avoids flipping the UI into a loading state during background polls.
 */


export const loadNotifications = createAsyncThunk(
  "notifications/load",
  async () => {
    return await fetchNotifications();
  }
);

/** Lightweight poll that only reconciles when the server-side unread count drifts. */
export const syncUnreadCount = createAsyncThunk(
  "notifications/syncUnreadCount",
  async (_, { getState, dispatch }) => {
    const serverUnread = await fetchUnreadCount();
    console.log("syncUnreadCount serverUnread:", serverUnread);
    const localUnread = selectUnreadCount(getState());
    const localTotal = getState().notifications.items?.length;

  // If counts diverge (a new notification arrived, or one was read elsewhere),
  // pull the fresh list so all three surfaces stay in sync.
  if (serverUnread !== localUnread || (serverUnread > 0 && localTotal === 0)) {
    await dispatch(loadNotifications({ silent: true }));
  }
  return serverUnread;
});

export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async (id) => {
    await markNotificationsRead([id]);
    return id;
  },
);

export const markAllNotificationsReadThunk = createAsyncThunk(
  "notifications/markAllRead",
  async () => {
    await markAllNotificationsRead();
  },
);

export const dismissNotification = createAsyncThunk(
  "notifications/dismiss",
  async (id) => {
    await deleteNotification(id);
    return id;
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Optimistic local updates — server calls are handled by the thunks above.
    notificationReadLocally(state, action) {
      const found = state.items?.find((n) => n.id === action.payload);
      if (found) found.read = true;
    },
    allNotificationsReadLocally(state) {
      state?.items?.forEach((n) => {
        n.read = true;
      });
    },
    notificationRemovedLocally(state, action) {
      state.items = state.items?.filter((n) => n.id !== action.payload);
    },
    resetNotifications() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadNotifications.pending, (state, action) => {
        if (!action.meta.arg?.silent) state.status = "loading";
        state.error = null;
      })
      .addCase(loadNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
        state.lastFetchedAt = Date.now();
      })
      .addCase(loadNotifications.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load notifications";
      })
      // Optimistic apply on pending so the UI reacts instantly.
      .addCase(markNotificationRead.pending, (state, action) => {
        const found = state.items?.find((n) => n.id === action.meta.arg);
        if (found) found.read = true;
      })
      .addCase(markAllNotificationsReadThunk.pending, (state) => {
        state.items?.forEach((n) => {
          n.read = true;
        });
      })
      .addCase(dismissNotification.pending, (state, action) => {
        state.items = state.items?.filter((n) => n.id !== action.meta.arg);
      });
  },
});

export const {
  notificationReadLocally,
  allNotificationsReadLocally,
  notificationRemovedLocally,
  resetNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectNotifications = (state) => {
  console.log("selectNotifications state:", state);
  return state.notifications?.items;
}
export const selectNotificationsStatus = (state) =>
  state.notifications?.status;

export const selectUnreadCount = createSelector(selectNotifications, (items) =>
  items?.filter((n) => !n.read)?.length,
);

export const selectCategoryCounts = createSelector(selectNotifications, (items) => {
  const counts = {
    All: items?.length,
    Alert: 0,
    Action: 0,
    Approval: 0,
    Update: 0,
    System: 0,
  };
  items?.forEach((n) => {
    counts[n.category] += 1;
  });
  return counts;
});
